import React, {
    FC,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';

import AampePopover from '../../components/shared/AampePopover';
import AampeTooltip from '../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

import { MessageContentContext } from '../../context/EditorContext';

import useGetFormulaMessageContents from '../../hooks/formulas/useGetFormulaMessageContent';
import useHandleQueryParams from '../../hooks/helper/useHandleQueryParams';
import useLanguagesQuery from '../../hooks/languages/useLanguagesQuery';
import useAddMessageContentToFormula from '../../hooks/message-content/useAddMessageContentToFormula';
import { useAuthSession } from 'hooks/useAuthSession';

import { LanguageModel } from '../../models/languages.model';
import { MessageContent } from '../../models/message-content.model';

interface FormulaLanguageSelectionType {
    allowLanguageSelection?: boolean;
    hasWriteAccess: boolean;
}

const FormulaLanguageSelection: FC<FormulaLanguageSelectionType> = ({
    allowLanguageSelection = true,
    hasWriteAccess,
}) => {
    const { user } = useAuthSession();
    const multilanguageEnabled = user?.multilanguage_enabled;

    const { formulaId } = useParams<{ formulaId: string }>();

    const anchorRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [queryParams, setQueryParams] = useHandleQueryParams();

    const { data: formulaMessageContents } = useGetFormulaMessageContents(
        +formulaId,
    );
    const { data } = useLanguagesQuery();
    const { isContentUpdating } = useContext(MessageContentContext);

    const { mutate: addMessageContentToFormula } =
        useAddMessageContentToFormula(+formulaId);

    const getLanguageName = useCallback(
        (languageId: string) => {
            return data?.find((language) => language.id === languageId)?.name;
        },
        [data],
    );

    const getMessageContentId = useCallback(
        (languageId: string) => {
            return formulaMessageContents?.find(
                (content) => content.language_id === languageId,
            )?.id;
        },
        [formulaMessageContents],
    );

    const setSelectedLanguage = useCallback(
        (languageId: string) => {
            if (isContentUpdating) {
                return;
            }
            setQueryParams({
                messageContentId: getMessageContentId(languageId),
            });
        },
        [setQueryParams, getMessageContentId, isContentUpdating],
    );

    const addLanguageToMessage = useCallback(
        (languageId: string) => {
            if (hasWriteAccess && !isContentUpdating) {
                addMessageContentToFormula(languageId);
            }
        },
        [addMessageContentToFormula, hasWriteAccess, isContentUpdating],
    );

    const defaultLanguage = useMemo<LanguageModel | undefined>(() => {
        return data?.find((language) => language.is_default);
    }, [data]);

    const messageContentId = useMemo<string | undefined>(() => {
        return queryParams.messageContentId as string;
    }, [queryParams]);

    const messageContent = useMemo<MessageContent | undefined>(() => {
        return formulaMessageContents?.find(
            (content) => content.id === messageContentId,
        );
    }, [formulaMessageContents, messageContentId]);

    const selectedLanguage = useMemo<string | undefined>(() => {
        if (messageContent) {
            return messageContent.language_id;
        }

        return defaultLanguage?.id;
    }, [messageContent, defaultLanguage]);

    const addedLanguages = useMemo<string[]>(() => {
        return (
            formulaMessageContents?.map((content) => content.language_id) ?? []
        );
    }, [formulaMessageContents]);

    const availableLanguages = useMemo<LanguageModel[]>(() => {
        return (data ?? []).filter(
            (language) => !addedLanguages.includes(language.id),
        );
    }, [data, addedLanguages]);

    const hasAvailableLanguages = useMemo<boolean>(() => {
        return availableLanguages.length > 0;
    }, [availableLanguages]);

    const isSelectLanguageDisabled = useMemo<boolean>(() => {
        return !hasAvailableLanguages || isContentUpdating;
    }, [hasAvailableLanguages, isContentUpdating]);

    if (!multilanguageEnabled) {
        return null;
    }

    return (
        <>
            <Container>
                {(addedLanguages ?? []).map((language) => (
                    <AampeTooltip
                        key={language}
                        title={isContentUpdating ? 'Updating...' : ''}
                        arrow
                        placement="bottom"
                    >
                        <LanguageItem
                            key={language}
                            disabled={isContentUpdating}
                            onClick={() => setSelectedLanguage(language)}
                            isSelected={selectedLanguage === language}
                        >
                            {getLanguageName(language)}
                        </LanguageItem>
                    </AampeTooltip>
                ))}
                {hasWriteAccess && allowLanguageSelection && (
                    <AampeTooltip
                        title={
                            !hasAvailableLanguages
                                ? 'No languages available'
                                : ''
                        }
                        arrow
                        placement="bottom"
                    >
                        <LanguageItem
                            ref={anchorRef}
                            disabled={isSelectLanguageDisabled}
                            onClick={() =>
                                !isSelectLanguageDisabled && setIsOpen(true)
                            }
                        >
                            Select Language <ChevronDownIcon size={20} />
                        </LanguageItem>
                    </AampeTooltip>
                )}
            </Container>
            <AampePopover
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                triggerRef={anchorRef}
                showArrow={false}
            >
                <div>
                    {availableLanguages.map((language) => (
                        <LanguageListItem
                            key={language.id}
                            onClick={() => {
                                addLanguageToMessage(language.id);
                                setIsOpen(false);
                            }}
                        >
                            {language.name}
                        </LanguageListItem>
                    ))}
                </div>
            </AampePopover>
        </>
    );
};

export default FormulaLanguageSelection;

const Container = styled.div({
    display: 'flex',
    backgroundColor: aampeTheme.colors.white,
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
});

const LanguageItem = styled.div<{ isSelected?: boolean; disabled?: boolean }>(
    ({ isSelected, disabled }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: aampeTheme.spacing4(2, 4),
        borderRight: `1px solid ${aampeTheme.colors.bordersWG01}`,
        backgroundColor: isSelected
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.white,
        color: isSelected
            ? aampeTheme.colors.white
            : aampeTheme.colors.neutral09,
        cursor: 'pointer',
        pointerEvents: disabled ? 'none' : 'auto',
    }),
);

const LanguageListItem = styled.span<{ disabled?: boolean }>(
    ({ disabled }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: aampeTheme.spacing4(2),
        borderRadius: aampeTheme.borderRadius.default,
        color: aampeTheme.colors.neutral09,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: aampeTheme.colors.interactiveWG01,
        },
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    }),
);

const ChevronDownIcon = styled(ICONS.chevronDown)({
    marginLeft: aampeTheme.spacing4(1),
});
